/**
 * System Service - API calls for MASTER-level operations
 */

import { api } from './api';
import type {
  Tenant,
  TenantStats,
  TenantAiUsage,
  SystemStats,
  CreateTenantDto,
  UpdateTenantDto,
  UpdateTenantTrialDto,
  CreateTenantUserDto,
  UpdateTenantUserDto,
  CreateMasterDto,
  UpdateMasterDto,
  TenantFilters,
  TenantUser,
  MasterUser,
  Organization,
  CreateOrganizationDto,
  UpdateOrganizationDto,
  OrganizationFilters,
  OrgUser,
  CreateOrgUserDto,
  OrgUserFilters,
  ProvisioningStatus,
  Plan,
  CreatePlanDto,
  UpdatePlanDto,
  PlanFilters,
  TenantBillingOverview,
  TenantBillingOverviewCustomer,
  SubscriptionCancelMode,
  CancelSubscriptionResponse,
  CancelPendingCancellationResponse,
  UpdateTenantSubscriptionDto,
  UpdateTenantSubscriptionResponse,
  CancelChargeResponse,
  UpdateTenantChargeDto,
  UpdateTenantChargeResponse,
  UpdateTenantBillingCustomerDto,
  DeleteTenantBillingCustomerDto,
  DeleteTenantBillingCustomerResponse,
  ResetTenantSubscriptionResponse,
} from '@/features/system/types/system.types';
import type {
  OrgDashboardSnapshot,
  OrgDashboardPeriod,
} from '@/features/system/types/org-dashboard.types';

/**
 * Resultado paginado retornado pelo backend do System module
 */
interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * System Overview
 */
export async function getSystemStats(): Promise<SystemStats> {
  const { data } = await api.get<SystemStats>('/system/overview');
  return data;
}

/**
 * Tenants Management
 */
export async function listTenants(
  filters?: TenantFilters,
): Promise<PaginatedResult<Tenant>> {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.q) params.append('q', filters.q);
    if (filters.isActive !== undefined)
      params.append('isActive', filters.isActive.toString());
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
  }

  const { data } = await api.get<PaginatedResult<Tenant>>(
    `/system/tenants?${params.toString()}`,
  );
  return data;
}

export async function getTenant(id: string): Promise<Tenant> {
  const { data } = await api.get<Tenant>(`/system/tenants/${id}`);
  return data;
}

export async function createTenant(dto: CreateTenantDto): Promise<Tenant> {
  const { data } = await api.post<Tenant>('/system/tenants', dto);
  return data;
}

export async function updateTenant(
  id: string,
  dto: UpdateTenantDto,
): Promise<Tenant> {
  const { data } = await api.patch<Tenant>(`/system/tenants/${id}`, dto);
  return data;
}

/**
 * Atualiza a data de expiração do trial de um tenant (#884).
 * Só é aceito quando o tenant está em trial (422 caso contrário) e a data
 * deve ser futura (validation error). ORG_OWNER only.
 */
export async function updateTenantTrial(
  id: string,
  dto: UpdateTenantTrialDto,
): Promise<Tenant> {
  const { data } = await api.patch<Tenant>(`/system/tenants/${id}/trial`, dto);
  return data;
}

export async function activateTenant(id: string): Promise<Tenant> {
  const { data } = await api.patch<Tenant>(`/system/tenants/${id}/activate`);
  return data;
}

export async function deactivateTenant(id: string): Promise<Tenant> {
  const { data } = await api.patch<Tenant>(`/system/tenants/${id}/deactivate`);
  return data;
}

export async function getTenantStats(id: string): Promise<TenantStats> {
  const { data } = await api.get<TenantStats>(`/system/tenants/${id}/stats`);
  return data;
}

/**
 * Uso de IA por tenant da organização no mês corrente (ATR-113).
 * Leads/créditos do período + teto de créditos + tokens agregados.
 * ORG_OWNER only (enforced no backend).
 */
export async function getOrgAiUsage(): Promise<TenantAiUsage[]> {
  const { data } = await api.get<TenantAiUsage[]>('/system/tenants/ai-usage');
  return data;
}

/**
 * Reatribui o billing owner (responsável financeiro) de um tenant.
 * O `userId` alvo deve ser um ADMIN que pertence ao tenant.
 * MASTER pode qualquer tenant; ORG_OWNER apenas tenants da própria org.
 */
export async function setTenantBillingOwner(
  id: string,
  userId: string,
): Promise<Tenant> {
  const { data } = await api.patch<Tenant>(
    `/system/tenants/${id}/billing-owner`,
    { userId },
  );
  return data;
}

/**
 * Define a isenção de pagamento (payment-exempt) de um tenant (#884).
 * Quando `exempt = true`, o tenant não passa pelo paywall de cobrança.
 * MASTER pode qualquer tenant; ORG_OWNER apenas tenants da própria org.
 */
export async function setPaymentExemption(
  tenantId: string,
  exempt: boolean,
): Promise<Tenant> {
  const { data } = await api.patch<Tenant>(
    `/system/tenants/${tenantId}/payment-exemption`,
    { exempt },
  );
  return data;
}

/**
 * Tenant Users Management
 */
export async function listTenantUsers(
  tenantId: string,
  params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  },
): Promise<PaginatedResult<TenantUser>> {
  const searchParams = new URLSearchParams();

  if (params) {
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.isActive !== undefined)
      searchParams.append('isActive', params.isActive.toString());
  }

  const queryString = searchParams.toString();
  const url = queryString
    ? `/system/tenants/${tenantId}/users?${queryString}`
    : `/system/tenants/${tenantId}/users`;

  const { data } = await api.get<PaginatedResult<TenantUser>>(url);
  return data;
}

export async function createTenantUser(
  tenantId: string,
  dto: CreateTenantUserDto,
): Promise<TenantUser> {
  const { data } = await api.post<TenantUser>(
    `/system/tenants/${tenantId}/users`,
    dto,
  );
  return data;
}

export async function updateTenantUser(
  tenantId: string,
  userId: string,
  dto: UpdateTenantUserDto,
): Promise<TenantUser> {
  const { data } = await api.patch<TenantUser>(
    `/system/tenants/${tenantId}/users/${userId}`,
    dto,
  );
  return data;
}

export async function deactivateTenantUser(
  tenantId: string,
  userId: string,
): Promise<{ message: string }> {
  const { data } = await api.patch<{ message: string }>(
    `/system/tenants/${tenantId}/users/${userId}/deactivate`,
  );
  return data;
}

export async function resetTenantUserPassword(
  tenantId: string,
  userId: string,
): Promise<{ message: string; temporaryPassword: string }> {
  const { data } = await api.post<{ message: string; temporaryPassword: string }>(
    `/system/tenants/${tenantId}/users/${userId}/reset-password`,
  );
  return data;
}

/**
 * Master Users Management
 */
export async function listMasters(params?: {
  page?: number;
  limit?: number;
  search?: string;
  isActive?: boolean;
}): Promise<PaginatedResult<MasterUser>> {
  const searchParams = new URLSearchParams();

  if (params) {
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.isActive !== undefined)
      searchParams.append('isActive', params.isActive.toString());
  }

  const queryString = searchParams.toString();
  const url = queryString ? `/system/masters?${queryString}` : '/system/masters';

  const { data } = await api.get<PaginatedResult<MasterUser>>(url);
  return data;
}

export async function createMaster(dto: CreateMasterDto): Promise<MasterUser> {
  const { data } = await api.post<MasterUser>('/system/masters', dto);
  return data;
}

export async function updateMaster(
  id: string,
  dto: UpdateMasterDto,
): Promise<MasterUser> {
  const { data } = await api.patch<MasterUser>(`/system/masters/${id}`, dto);
  return data;
}

export async function deactivateMaster(id: string): Promise<{ message: string }> {
  const { data } = await api.patch<{ message: string }>(
    `/system/masters/${id}/deactivate`,
  );
  return data;
}

/**
 * Organizations Management
 */
export async function listOrganizations(
  filters?: OrganizationFilters,
): Promise<PaginatedResult<Organization>> {
  const params = new URLSearchParams();

  if (filters) {
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.q) params.append('q', filters.q);
    if (filters.status) params.append('status', filters.status);
    if (filters.tier) params.append('tier', filters.tier);
  }

  // Backend returns OrganizationResponseDto[] (array), not a paginated result.
  // We adapt it into a PaginatedResult for consistent frontend consumption.
  const { data } = await api.get<Organization[]>(
    `/organizations?${params.toString()}`,
  );

  const page = filters?.page ?? 1;
  const limit = filters?.limit ?? data.length;
  const total = data.length;
  const totalPages = limit > 0 ? Math.ceil(total / limit) : 0;

  return { data, total, page, limit, totalPages };
}

export async function getOrganization(id: string): Promise<Organization> {
  const { data } = await api.get<Organization>(`/organizations/${id}`);
  return data;
}

export async function createOrganization(
  dto: CreateOrganizationDto,
): Promise<Organization> {
  const { data } = await api.post<Organization>('/organizations', dto);
  return data;
}

export async function updateOrganization(
  id: string,
  dto: UpdateOrganizationDto,
): Promise<Organization> {
  const { data } = await api.patch<Organization>(`/organizations/${id}`, dto);
  return data;
}

export async function activateOrganization(id: string): Promise<Organization> {
  const { data } = await api.post<Organization>(
    `/organizations/${id}/activate`,
  );
  return data;
}

export async function suspendOrganization(id: string): Promise<Organization> {
  const { data } = await api.post<Organization>(
    `/organizations/${id}/suspend`,
  );
  return data;
}

/**
 * Organization Provisioning Saga
 */
export async function provisionOrganization(
  id: string,
): Promise<{ jobId: string }> {
  const { data } = await api.post<{ jobId: string }>(
    `/organizations/${id}/provision`,
  );
  return data;
}

export async function getProvisioningStatus(
  id: string,
): Promise<ProvisioningStatus> {
  const { data } = await api.get<ProvisioningStatus>(
    `/organizations/${id}/provision/status`,
  );
  return data;
}

export async function retryProvisioning(
  id: string,
): Promise<{ jobId: string }> {
  const { data } = await api.post<{ jobId: string }>(
    `/organizations/${id}/provision/retry`,
  );
  return data;
}

/**
 * Organization Users Management
 */
export async function listOrgUsers(
  orgId: string,
  params?: OrgUserFilters,
): Promise<PaginatedResult<OrgUser>> {
  const searchParams = new URLSearchParams();

  if (params) {
    if (params.page) searchParams.append('page', params.page.toString());
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.search) searchParams.append('search', params.search);
    if (params.role) searchParams.append('role', params.role);
  }

  const queryString = searchParams.toString();
  const url = queryString
    ? `/organizations/${orgId}/users?${queryString}`
    : `/organizations/${orgId}/users`;

  const { data } = await api.get<PaginatedResult<OrgUser>>(url);
  return data;
}

export async function createOrgUser(
  orgId: string,
  dto: CreateOrgUserDto,
): Promise<OrgUser> {
  const { data } = await api.post<OrgUser>(
    `/organizations/${orgId}/users`,
    dto,
  );
  return data;
}

export async function deactivateOrgUser(
  orgId: string,
  userId: string,
): Promise<{ message: string }> {
  const { data } = await api.patch<{ message: string }>(
    `/organizations/${orgId}/users/${userId}/deactivate`,
  );
  return data;
}

export interface OrgTenant {
  id: string;
  name: string;
  slug: string;
}

export async function listOrgTenants(
  orgId: string,
): Promise<OrgTenant[]> {
  const { data } = await api.get<OrgTenant[]>(
    `/organizations/${orgId}/users/tenants`,
  );
  return data;
}

/**
 * Tenant User Password Management
 */
export async function changeTenantUserPassword(
  tenantId: string,
  userId: string,
  newPassword: string,
): Promise<{ message: string }> {
  const { data } = await api.patch<{ message: string }>(
    `/system/tenants/${tenantId}/users/${userId}/change-password`,
    { newPassword },
  );
  return data;
}

export async function sendTenantUserMagicLink(
  tenantId: string,
  userId: string,
): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>(
    `/system/tenants/${tenantId}/users/${userId}/magic-link`,
  );
  return data;
}

/**
 * Organization User Password Management
 */
export async function changeOrgUserPassword(
  orgId: string,
  userId: string,
  newPassword: string,
): Promise<{ message: string }> {
  const { data } = await api.patch<{ message: string }>(
    `/organizations/${orgId}/users/${userId}/change-password`,
    { newPassword },
  );
  return data;
}

export async function resetOrgUserPassword(
  orgId: string,
  userId: string,
): Promise<{ message: string; temporaryPassword: string }> {
  const { data } = await api.post<{ message: string; temporaryPassword: string }>(
    `/organizations/${orgId}/users/${userId}/reset-password`,
  );
  return data;
}

export async function sendOrgUserMagicLink(
  orgId: string,
  userId: string,
): Promise<{ message: string }> {
  const { data } = await api.post<{ message: string }>(
    `/organizations/${orgId}/users/${userId}/magic-link`,
  );
  return data;
}

/**
 * Org Owner Dashboard (#org-dashboard)
 *
 * Snapshot agregado cross-tenant da organização (receita/billing, CRM,
 * mensageria, usuários e roster por-tenant). MASTER/ORG_OWNER only — o backend
 * resolve a org via JWT (ORG_OWNER) ou via `x-selected-organization-id`
 * (MASTER), header já injetado pelo wrapper axios em rotas `/system/*`.
 */
export async function getOrgDashboard(
  period: OrgDashboardPeriod = '30d',
): Promise<OrgDashboardSnapshot> {
  const { data } = await api.get<OrgDashboardSnapshot>(
    `/system/org-dashboard?period=${period}`,
  );
  return data;
}

/**
 * Plans Management (catálogo de planos — PR2a backend)
 *
 * ORG_OWNER only. O backend resolve a org via JWT (ou via header
 * `x-selected-organization-id` injetado pelo wrapper axios em rotas
 * `/system/*`). Valores monetários sempre em centavos.
 */
export async function listPlans(filters?: PlanFilters): Promise<Plan[]> {
  const params = new URLSearchParams();
  if (filters?.includeInactive !== undefined) {
    params.append('includeInactive', filters.includeInactive.toString());
  }
  const queryString = params.toString();
  const url = queryString ? `/system/plans?${queryString}` : '/system/plans';
  const { data } = await api.get<Plan[]>(url);
  return data;
}

export async function getPlan(id: string): Promise<Plan> {
  const { data } = await api.get<Plan>(`/system/plans/${id}`);
  return data;
}

export async function createPlan(dto: CreatePlanDto): Promise<Plan> {
  const { data } = await api.post<Plan>('/system/plans', dto);
  return data;
}

export async function updatePlan(
  id: string,
  dto: UpdatePlanDto,
): Promise<Plan> {
  const { data } = await api.patch<Plan>(`/system/plans/${id}`, dto);
  return data;
}

/** Descontinua o plano (status → `legacy`). */
export async function deprecatePlan(id: string): Promise<Plan> {
  const { data } = await api.post<Plan>(`/system/plans/${id}/deprecate`);
  return data;
}

export async function deletePlan(id: string): Promise<void> {
  await api.delete(`/system/plans/${id}`);
}

/**
 * Tenant Billing Overview (ATR-105 — aba "Faturamento" read-only do painel).
 *
 * `GET /organizations/:orgId/billing/tenants/:tenantId` retorna o overview
 * agregado (customer + assinatura + invoices) montado a partir do mirror local
 * de billing. Valores monetários em centavos. MASTER/ORG_OWNER only — o backend
 * reforça o escopo da org via `:orgId` no path (`assertOrgOwnership`).
 *
 * Sem customer/subscription NÃO é 404: os campos voltam `null`.
 */
export async function getTenantBillingOverview(
  orgId: string,
  tenantId: string,
): Promise<TenantBillingOverview> {
  const { data } = await api.get<TenantBillingOverview>(
    `/organizations/${orgId}/billing/tenants/${tenantId}`,
  );
  return data;
}

/**
 * Base do path das ações de billing por tenant (ATR-106):
 * `/organizations/:orgId/billing/tenants/:tenantId`.
 */
function tenantBillingBase(orgId: string, tenantId: string): string {
  return `/organizations/${orgId}/billing/tenants/${tenantId}`;
}

/**
 * Cancela a assinatura do tenant (ATR-106).
 * - `immediate` cancela agora no gateway e marca o mirror CANCELLED.
 * - `end_of_cycle` agenda o cancelamento para a virada do ciclo.
 */
export async function cancelTenantSubscription(
  orgId: string,
  tenantId: string,
  mode: SubscriptionCancelMode,
): Promise<CancelSubscriptionResponse> {
  const { data } = await api.post<CancelSubscriptionResponse>(
    `${tenantBillingBase(orgId, tenantId)}/subscription/cancel`,
    { mode },
  );
  return data;
}

/** Desfaz o cancelamento agendado (fim-de-ciclo) da assinatura (ATR-106). */
export async function undoTenantSubscriptionCancellation(
  orgId: string,
  tenantId: string,
): Promise<CancelPendingCancellationResponse> {
  const { data } = await api.delete<CancelPendingCancellationResponse>(
    `${tenantBillingBase(orgId, tenantId)}/subscription/pending-cancel`,
  );
  return data;
}

/**
 * Edita administrativamente a assinatura do tenant (valor/ciclo/vencimento).
 * Update esparso: envie apenas os campos alterados (ATR-106).
 */
export async function updateTenantSubscription(
  orgId: string,
  tenantId: string,
  dto: UpdateTenantSubscriptionDto,
): Promise<UpdateTenantSubscriptionResponse> {
  const { data } = await api.patch<UpdateTenantSubscriptionResponse>(
    `${tenantBillingBase(orgId, tenantId)}/subscription`,
    dto,
  );
  return data;
}

/** Cancela uma cobrança pendente do tenant (sem estorno de pagas) (ATR-106). */
export async function cancelTenantCharge(
  orgId: string,
  tenantId: string,
  invoiceId: string,
): Promise<CancelChargeResponse> {
  const { data } = await api.post<CancelChargeResponse>(
    `${tenantBillingBase(orgId, tenantId)}/charges/${invoiceId}/cancel`,
  );
  return data;
}

/**
 * Edita uma cobrança do tenant (valor/vencimento/descrição). Update esparso:
 * envie apenas os campos alterados. A resposta pode trazer `warning` (ATR-106).
 */
export async function updateTenantCharge(
  orgId: string,
  tenantId: string,
  invoiceId: string,
  dto: UpdateTenantChargeDto,
): Promise<UpdateTenantChargeResponse> {
  const { data } = await api.patch<UpdateTenantChargeResponse>(
    `${tenantBillingBase(orgId, tenantId)}/charges/${invoiceId}`,
    dto,
  );
  return data;
}

/**
 * Edita os dados cadastrais do customer do tenant (nome/e-mail/telefone/
 * endereço). Identidade fiscal (CPF/CNPJ) NÃO é editável (ATR-106).
 */
export async function updateTenantBillingCustomer(
  orgId: string,
  tenantId: string,
  dto: UpdateTenantBillingCustomerDto,
): Promise<TenantBillingOverviewCustomer> {
  const { data } = await api.patch<TenantBillingOverviewCustomer>(
    `${tenantBillingBase(orgId, tenantId)}/customer`,
    dto,
  );
  return data;
}

/**
 * Deleta o customer do tenant. `deleteInAsaas` controla a remoção no gateway;
 * `keepLocalMirror` (default true) mantém o espelho local (ATR-106).
 */
export async function deleteTenantBillingCustomer(
  orgId: string,
  tenantId: string,
  dto: DeleteTenantBillingCustomerDto,
): Promise<DeleteTenantBillingCustomerResponse> {
  const { data } = await api.delete<DeleteTenantBillingCustomerResponse>(
    `${tenantBillingBase(orgId, tenantId)}/customer`,
    { data: dto },
  );
  return data;
}

/**
 * Reseta o estado de billing CANCELADO do tenant (ATR-255): apaga assinaturas,
 * cobranças e resgates de cupom no espelho local, cancelando antes o que houver
 * no gateway, MANTENDO o customer para que o cliente possa assinar de novo. Não
 * afeta dados do CRM.
 */
export async function resetTenantSubscription(
  orgId: string,
  tenantId: string,
): Promise<ResetTenantSubscriptionResponse> {
  const { data } = await api.post<ResetTenantSubscriptionResponse>(
    `${tenantBillingBase(orgId, tenantId)}/subscription/reset`,
  );
  return data;
}

export const systemService = {
  getSystemStats,
  getOrgDashboard,
  listPlans,
  getPlan,
  createPlan,
  updatePlan,
  deprecatePlan,
  deletePlan,
  getTenantBillingOverview,
  cancelTenantSubscription,
  undoTenantSubscriptionCancellation,
  updateTenantSubscription,
  cancelTenantCharge,
  updateTenantCharge,
  updateTenantBillingCustomer,
  deleteTenantBillingCustomer,
  resetTenantSubscription,
  listTenants,
  getTenant,
  createTenant,
  updateTenant,
  updateTenantTrial,
  activateTenant,
  deactivateTenant,
  getTenantStats,
  getOrgAiUsage,
  setTenantBillingOwner,
  setPaymentExemption,
  listTenantUsers,
  createTenantUser,
  updateTenantUser,
  deactivateTenantUser,
  resetTenantUserPassword,
  changeTenantUserPassword,
  sendTenantUserMagicLink,
  listMasters,
  createMaster,
  updateMaster,
  deactivateMaster,
  listOrganizations,
  getOrganization,
  createOrganization,
  updateOrganization,
  activateOrganization,
  suspendOrganization,
  provisionOrganization,
  getProvisioningStatus,
  retryProvisioning,
  listOrgUsers,
  createOrgUser,
  deactivateOrgUser,
  changeOrgUserPassword,
  resetOrgUserPassword,
  sendOrgUserMagicLink,
  listOrgTenants,
};
